<template>
    <Head title="Applicant Certification"></Head>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <Toast />
                <Toolbar class="mb-4">
                    <template v-slot:start>
                        <div class="my-2">
                            <Button
                                label="New"
                                icon="pi pi-plus"
                                class="p-button-success mr-2"
                                @click="openNew"
                            />
                            <Button
                                label="Delete"
                                icon="pi pi-trash"
                                class="p-button-danger"
                                @click="confirmDeleteSelected"
                                :disabled="
                                    !selectedModels || !selectedModels.length
                                "
                            />
                        </div>
                    </template>
                </Toolbar>

                <DataTable
                    ref="dt"
                    :value="models"
                    dataKey="id"
                    v-model:selection="selectedModels"
                    :paginator="true"
                    :rows="10"
                    :loading="loading"
                    :total-records="totalRecords"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    :rowsPerPageOptions="[5, 10, 25]"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} models"
                    responsiveLayout="scroll"
                    :selectAll="selectAll"
                    @row-select="onRowSelect"
                    @row-unselect="onRowUnselect"
                >
                    <template #header>
                        <div
                            class="flex flex-column md:flex-row md:justify-content-between md:align-items-center"
                        >
                            <h5 class="m-0">Manage Certification</h5>
                            <span class="block mt-2 md:mt-0 p-input-icon-left">
                                <i class="pi pi-search" />
                                <InputText
                                    v-model="filters['global'].value"
                                    placeholder="Search..."
                                />
                            </span>
                        </div>
                    </template>

                    <Column
                        selectionMode="multiple"
                        headerStyle="width: 3rem"
                    ></Column>
                    <Column
                        field="code"
                        header="Code"
                        :sortable="true"
                        headerStyle="width:5%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Id</span>
                            {{ slotProps.data.id }}
                        </template>
                    </Column>
                    <Column
                        field="name"
                        header="Name"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Name</span>
                            <Link :href="slotProps.data.credential_url">{{ slotProps.data.name }} - {{ slotProps.data.credential_id }}</Link>
                        </template>
                    </Column>
                    <Column
                        field="issuer"
                        header="Issuer"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Issuer</span>
                            {{ slotProps.data.issuer }}
                        </template>
                    </Column>
                    <Column
                        field="issued_date"
                        header="Issued Date"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Issued Date</span>
                            {{ slotProps.data.issued_date }}
                        </template>
                    </Column>
                    <Column
                        field="expired_date"
                        header="Expired Date"
                        :sortable="true"
                        headerStyle="width:70%; min-width:10rem;"
                    >
                        <template #body="slotProps">
                            <span class="p-column-title">Expired Date</span>
                            {{ slotProps.data.expired_date }}
                        </template>
                    </Column>
                    <Column headerStyle="width:15%;min-width:10rem;">
                        <template #body="slotProps">
                            <Button
                                icon="pi pi-pencil"
                                class="p-button-rounded p-button-success mr-2"
                                @click="editModel(slotProps.data)"
                            />
                            <Button
                                icon="pi pi-trash"
                                class="p-button-rounded p-button-warning mt-2"
                                @click="confirmDeleteModel(slotProps.data)"
                            />
                        </template>
                    </Column>
                </DataTable>

                <Dialog
                    v-model:visible="modelDialog"
                    :style="{ width: '450px' }"
                    header="Certification Detail"
                    :modal="true"
                    class="p-fluid"
                >
                    <div class="field">
                        <label for="name">Name</label>
                        <InputText
                            id="name"
                            v-model.trim="model.name"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.name }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.name"
                            >Name is required.</small
                        >
                    </div>
                     <div class="field">
                        <label for="Issuer">Issuer</label>
                        <InputText
                            id="Issuer"
                            v-model.trim="model.issuer"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.issuer }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.issuer"
                            >Issuer is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="Issuer">Issued Date</label>
                        <Calendar :showIcon="true"
                            :showButtonBar="true"
                            v-model="model.issued_date"
                            :class="{'p-invalid' : submitted && !model.issued_date}"
                            dateFormat="yy-mm-dd">
                        </Calendar>
                        <small class="p-invalid" v-if="submitted && !model.issued_date"
                            >Expired Date is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="Issuer">Expired Date</label>
                        <Calendar :showIcon="true"
                            :showButtonBar="true"
                            v-model="model.expired_date"
                            :class="{'p-invalid' : submitted && !model.expired_date}"
                            dateFormat="yy-mm-dd">
                        </Calendar>
                        <small class="p-invalid" v-if="submitted && !model.expired_date"
                            >Expired Date is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="credential_id">Credential ID</label>
                        <InputText
                            id="credential_id"
                            v-model.trim="model.credential_id"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.credential_id }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.credential_id"
                            >Expired Date is required.</small
                        >
                    </div>
                    <div class="field">
                        <label for="credential_id">Credential URL</label>
                        <InputText
                            id="credential_url"
                            v-model.trim="model.credential_url"
                            required="true"
                            autofocus
                            :class="{ 'p-invalid': submitted && !model.credential_url }"
                        />
                        <small class="p-invalid" v-if="submitted && !model.credential_url"
                            >Expired Date is required.</small
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="hideDialog"
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="saveModel"
                        />
                    </template>
                </Dialog>

                <Dialog
                    v-model:visible="deleteModelDialog"
                    :style="{ width: '450px' }"
                    header="Confirm"
                    :modal="true"
                >
                    <div class="flex align-items-center justify-content-center">
                        <i
                            class="pi pi-exclamation-triangle mr-3"
                            style="font-size: 2rem"
                        />
                        <span v-if="model"
                            >Are you sure you want to delete
                            <b>{{ model.name }}</b
                            >?</span
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="deleteModelDialog = false"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="deleteModel"
                        />
                    </template>
                </Dialog>

                <Dialog
                    v-model:visible="deleteModelsDialog"
                    :style="{ width: '450px' }"
                    header="Confirm"
                    :modal="true"
                >
                    <div class="flex align-items-center justify-content-center">
                        <i
                            class="pi pi-exclamation-triangle mr-3"
                            style="font-size: 2rem"
                        />
                        <span v-if="model"
                            >Are you sure you want to delete the selected
                            models?</span
                        >
                    </div>
                    <template #footer>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            class="p-button-text"
                            @click="deleteModelsDialog = false"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            class="p-button-text"
                            @click="deleteSelectedModels"
                        />
                    </template>
                </Dialog>
            </div>
        </div>
    </div>
</template>
<script>
import axios from "axios";
import { Head } from "@inertiajs/inertia-vue3";
import { FilterMatchMode } from "primevue/api";
import { Link } from '@inertiajs/inertia-vue3';
import DataTable from "primevue/datatable";
import Calendar from 'primevue/calendar';
import Column from "primevue/column";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Toolbar from "primevue/toolbar";
import Toast from "primevue/toast";
import InputText from "primevue/inputtext";

export default {
    components: {
        Head,
        Link,
        DataTable,
        Calendar,
        Column,
        Dialog,
        Button,
        Toolbar,
        Toast,
        InputText,
    },
    data() {
        return {
            applicant_id: this.$page.props.candidate.data.id,
            searchFilter: "",
            loading: true,
            totalRecords: 0,
            selectedModel: null,
            filters: null,
            model: {},
            modelDialog: false,
            models: this.$page.props.candidate.data.certifications,
            deleteModelDialog: false,
            deleteModelsDialog: false,
            selectedModels: null,
            submitted: false,
            selectAll: false,
        };
    },
    created() {
        this.initFilters();
    },
    mounted() {
        this.loading = false;
    },
    methods: {
        onRowSelect() {
            this.selectAll = this.selectedModels.length === this.totalRecord;
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        openNew() {
            this.model = {};
            this.submitted = false;
            this.modelDialog = true;
        },
        hideDialog() {
            this.modelDialog = false;
            this.submitted = false;
        },
        saveModel() {
            this.submitted = true;
            this.model.applicant_id = this.applicant_id;
            if (this.model) {
                if (this.model.id) {
                    this.models[this.findIndexById(this.model.id)] = this.model;
                    axios
                        .put(this.route('applicant-certifications.destroy', this.model.id), this.model)
                        .then((response) => {
                            this.$toast.add({
                                severity: "success",
                                summary: "Successful",
                                detail: "Formal Education Updated",
                                life: 3000,
                            });
                        })
                        .catch((error) => {
                            this.$toast.add({
                                severity: "error",
                                summary: "Error",
                                detail: "Formal Education not updated",
                                life: 3000,
                            });
                        });
                }
                // If empty then add new record
                else {
                    axios
                        .post(this.route('applicant-certifications.destroy'), this.model)
                        .then((response) => {
                            this.model = response.data.data;
                            this.models.unshift(this.model);
                            this.$toast.add({
                                severity: "success",
                                summary: "Successful",
                                detail: "Formal Education Created",
                                life: 3000,
                            });
                        })
                        .catch((error) => {
                            this.$toast.add({
                                severity: "error",
                                summary: "Error",
                                detail: "Formal Education not created",
                                life: 3000,
                            });
                        });
                }
                this.modelDialog = false;
                this.model = {};
            }
        },
        editModel(model) {
            this.model = { ...model };
            this.modelDialog = true;
        },
        confirmDeleteModel(model) {
            this.model = model;
            this.deleteModelDialog = true;
        },
        deleteModel() {
            axios
                .delete(this.route('applicant-certifications.destroy', this.model.id))
                .then((response) => {
                    this.$toast.add({
                        severity: "success",
                        summary: "Successful",
                        detail: "Relative Deleted",
                        life: 3000,
                    });
                    this.models = this.models.filter(val => val.id !== this.model.id);
                    this.deleteModelDialog = false;
                    this.model = {};
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: "error",
                        summary: "Error",
                        detail: "Relative not deleted",
                        life: 3000,
                    });
                });
        },
        confirmDeleteSelected() {
            this.deleteModelsDialog = true;
        },
        deleteSelectedModels() {
            this.models = this.models.filter(
                (val) => !this.selectedModels.includes(val)
            );
            this.deleteModelsDialog = false;
            this.selectedModels = null;
            this.$toast.add({
                severity: "success",
                summary: "Successful",
                detail: "Models Deleted",
                life: 3000,
            });
        },
        findIndexById(id) {
			let index = -1;
			for (let i = 0; i < this.models.length; i++) {
				if (this.models[i].id === id) {
					index = i;
					break;
				}
			}
			return index;
		},
        initFilters(){
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            };
        },
    },
};
</script>
